import { useRef, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  DRAWER_TRANSITION_DURATION_CSS_VAR,
  DRAWER_TRANSITION_DURATION_MS
} from '../../constants/drawerConstants';
import { useDrawerDialog } from '../../hooks/useDrawerDialog';
import { useScrollLock } from '../../hooks/useScrollLock';
import styles from './DrawerShell.module.css';

// Published on the panel so the slide below, every transition inside a drawer,
// and the JS timers that wait on them all read one duration.
const transitionStyle = {
  [DRAWER_TRANSITION_DURATION_CSS_VAR]: `${DRAWER_TRANSITION_DURATION_MS}ms`
} as CSSProperties;

interface DrawerShellProps {
  isOpen: boolean;
  onClose: () => void;
  /** Fallback accessible name, used when `titleId` resolves to empty text. */
  ariaLabel: string;
  /** Id the consumer puts on its visible title element. */
  titleId?: string;
  /** Header content. The header row — and its close button — render only when supplied. */
  header?: ReactNode;
  children?: ReactNode;
}

/**
 * The chrome both drawers share: a full-page overlay, a fixed slide-in panel on
 * the right, and a header row with the close button — portal'd to <body> so no
 * ancestor's overflow or stacking context can clip it. Dialog semantics and
 * focus management come from `useDrawerDialog`.
 *
 * The panel stays mounted while closed so its slide transition can run; `inert`
 * is what keeps a closed drawer's controls off the tab order.
 */
export const DrawerShell = ({
  isOpen,
  onClose,
  ariaLabel,
  titleId,
  header,
  children
}: DrawerShellProps) => {
  const { t } = useTranslation();
  const drawerRef = useRef<HTMLElement>(null);

  useScrollLock(isOpen, drawerRef);
  useDrawerDialog(isOpen, drawerRef);

  return createPortal(
    <>
      {isOpen && <div className={styles.overlay} onClick={onClose} aria-hidden="true" />}
      <aside
        ref={drawerRef}
        className={`${styles.drawer} ${isOpen ? styles.open : ''}`}
        style={transitionStyle}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-label={ariaLabel}
        inert={!isOpen}
      >
        {header && (
          <div className={styles.drawerHeader}>
            <div>{header}</div>
            <button
              className={styles.closeButton}
              onClick={onClose}
              data-autofocus
              aria-label={t('close-details')}
            >
              &times;
            </button>
          </div>
        )}
        {children}
      </aside>
    </>,
    document.body
  );
};
